import type { AxiosRequestConfig } from 'axios';
import api from './api';
import { serverErrorMessage } from '../utils/extractApiError';
import type { IceServer } from '../utils/PeerConnection';

/**
 * The admin 1:1 test session — a real empathy session created directly, with an
 * optional Node bot in seat B, so one phone can test the whole call.
 *
 * Registered only in local/testing/staging; production has no such routes, so
 * every call here 404s there by construction rather than by a flag this file
 * would have to keep in step.
 */

/**
 * `bot_starting` | `bot_dispatched` | `no_bot` — the server's own words, and
 * the whole set.
 *
 * `bot_starting` and `bot_dispatched` are deliberately different: "it is coming"
 * and "it is already here" are different answers, and treating a bot that has
 * not beaten yet as a missing one is what let a second press put two bots into
 * one call (Codex #888 P1, id 4001274107).
 *
 * `bot_unavailable` is NOT here: the server refuses (422) rather than handing
 * back a session whose seat B is a bot it cannot start, because `show` admits
 * only the two stored participants and no real person could ever take that seat
 * (Codex #888 P2, id 4001274113). It arrives as an error, never as a status.
 */
export type BotStatus = 'bot_starting' | 'bot_dispatched' | 'no_bot';

/**
 * Where the bot is in its life, as the server sees it. `null` when seat B is a
 * real person and there is no bot to be anywhere.
 */
export type BotPhase = 'queued' | 'starting' | 'running' | 'lost';

export interface TestSessionSummary {
  id: number;
  status: string;
  ended_at: string | null;
  expires_at: string | null;
  /** The giver is the initiator, here as everywhere else in this codebase. */
  is_initiator: boolean;
  role: 'giver' | 'seeker' | null;
  partner_user_id: number | null;
  partner_name: string | null;
  /** The public handle — `/u/{username}`. Null for the bot, which has no page. */
  partner_username: string | null;
  partner_avatar_url: string | null;
  partner_is_bot: boolean;
  /**
   * The session's deadline, the same ISO instant the queue's status payloads
   * carry. Null on a session created before the cap existed.
   */
  ends_at: string | null;
  /**
   * How long this call may run in total, seconds — the deadline's sibling, off
   * the same `EmpathySession::capSeconds()` the queue's payloads read. The
   * ending's SHAPE is scaled to it, and an admin test call is normally armed
   * with the three-minute dev cap, so null here would mean the ninety-minute
   * wrap-up copy on a three-minute call.
   */
  cap_seconds: number | null;
  bot_phase: BotPhase | null;
  /**
   * WHY THERE IS NO PARTNER, IN THE SERVER'S OWN WORDS.
   *
   * The 2026-09-14 device pass ended at "Connection failed. Please try again."
   * — PeerAudioRoom's generic timeout — while the real answer ("the bot could
   * not reach the API at http://localhost:8000/api/v1") sat in a log file on
   * the machine nobody was looking at. A refusal that does not reach whoever
   * asked is not a refusal (class B), so it travels on the session itself and
   * is printed verbatim rather than re-worded here.
   */
  bot_failure_code: string | null;
  bot_failure_message: string | null;
}

export interface StartedTestSession {
  status: BotStatus;
  /** Extra detail from the server, when it has any. */
  reason: string | null;
  session: TestSessionSummary;
  links: { seat_a: string; seat_b: string };
}

export const startEmpathyTestSession = async (
  options: { withBot?: boolean; seatBUserId?: number; capMinutes?: number | null } = {},
): Promise<StartedTestSession> => {
  const response = await api.post('/admin/empathy-test-session', {
    with_bot: options.withBot ?? false,
    ...(options.seatBUserId ? { seat_b_user_id: options.seatBUserId } : {}),
    // OMITTED, not null. The server REFUSES the parameter outright outside
    // local/testing/staging, so a `cap_minutes: null` riding along on every
    // request would turn "I did not ask for a cap" into a 403 — see
    // `App\Support\DevCapOverride`, which keys off `has()`.
    ...(options.capMinutes ? { cap_minutes: options.capMinutes } : {}),
  });
  return response.data;
};

/**
 * THREE OUTCOMES, BECAUSE THERE ARE THREE (class B).
 *
 * "Here is your call", "this session is over, and here is why" and "I could not
 * ask" are different things to put in front of a person, and a thrown axios
 * error flattens the middle one into the last. The 409 body carries the
 * session summary on purpose — a bot that died carries its reason in exactly
 * that response, and throwing it away is how "Connection failed" happened.
 */
export type TestSessionRead =
  | { state: 'ready'; session: TestSessionSummary; iceServers: IceServer[] }
  | { state: 'ended'; session: TestSessionSummary | null; message: string }
  | { state: 'error'; message: string };

const serverSentence = (err: unknown): string | null =>
  serverErrorMessage((err as { response?: { data?: unknown } })?.response?.data);

export const readEmpathyTestSession = async (sessionId: number): Promise<TestSessionRead> => {
  try {
    const response = await api.get(`/empathy/test-session/${sessionId}`);
    return {
      state: 'ready',
      session: response.data.session,
      iceServers: response.data.ice_servers ?? [],
    };
  } catch (err) {
    const response = (err as { response?: { status?: number; data?: Record<string, unknown> } })
      ?.response;

    if (response?.status === 409) {
      const session = (response.data?.session as TestSessionSummary | undefined) ?? null;

      // The bot's own sentence outranks the generic one: "that test session has
      // already ended" is true of every one of these and useful about none.
      return {
        state: 'ended',
        session,
        message:
          session?.bot_failure_message ??
          serverSentence(err) ??
          'That test session has already ended.',
      };
    }

    return { state: 'error', message: serverSentence(err) ?? 'Could not open that test session.' };
  }
};

/**
 * What leaving actually did. Two outcomes, because "the row is closed" and
 * "I am the one who closed it" are different facts and only the first one is
 * what the page needs before it may say the call is over.
 */
export type LeaveOutcome = 'ended' | 'already_ended';

/**
 * Either seat can end it, and a failure REACHES THE CALLER (class B).
 *
 * This used to flatten every error into `false`, which the page then ignored —
 * so a network drop, a 5xx or a rejected request still rendered "Test session
 * ended", while the server row and the Node bot went on living until the
 * five-minute expiry sweep. A `boolean` the caller may ignore is not a refusal
 * anybody hears.
 *
 * A 409 is deliberately NOT a failure: the server is saying the session had
 * already ended, which is precisely the state the caller was asking for. It is
 * reported as its own outcome rather than thrown, so the page can end cleanly
 * without a retry affordance for something that needs no retrying. Everything
 * else — no network, 5xx, 403, a rejected promise of any shape — propagates
 * with the original error intact so the caller can show the server's own
 * sentence and offer to try again.
 */
export const leaveEmpathyTestSession = async (
  sessionId: number,
  config?: AxiosRequestConfig,
): Promise<LeaveOutcome> => {
  try {
    const url = `/empathy/test-session/${sessionId}/leave`;
    await (config ? api.post(url, undefined, config) : api.post(url));
    return 'ended';
  } catch (err) {
    if ((err as { response?: { status?: number } })?.response?.status === 409) {
      return 'already_ended';
    }
    throw err;
  }
};
